import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { api } from '../services/api'
import { AUTH_CALLBACK_ERROR } from '../components/AuthUrlBootstrap'

export default function Login() {
    const location = useLocation()
    const callbackMsg =
        (location.state as Record<string, string> | null)?.[AUTH_CALLBACK_ERROR] ?? ''
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState(callbackMsg)
    const [loading, setLoading] = useState(false)
    const [biometricLoading, setBiometricLoading] = useState(false)
    const [showResetPassword, setShowResetPassword] = useState(false)
    const [resetEmail, setResetEmail] = useState('')
    const [resetLoading, setResetLoading] = useState(false)
    const [resetMessage, setResetMessage] = useState('')
    const [resetError, setResetError] = useState('')

    const { login, biometric, loginWithBiometric } = useAuth()
    const navigate = useNavigate()

    useEffect(() => {
        if (!callbackMsg) return
        navigate(location.pathname + location.search, { replace: true, state: {} })
    }, [callbackMsg, location.pathname, location.search, navigate])

    const handleBiometricLogin = useCallback(async () => {
        setError('')
        setBiometricLoading(true)
        const result = await loginWithBiometric()
        if (result.error) {
            setError(result.error)
            setBiometricLoading(false)
        } else {
            navigate('/')
        }
    }, [loginWithBiometric, navigate])

    // Auto-prompt biometric on mount if available and enabled
    useEffect(() => {
        if (biometric.isAvailable && biometric.isEnabled) {
            const timer = setTimeout(() => {
                handleBiometricLogin()
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [biometric.isAvailable, biometric.isEnabled, handleBiometricLogin])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        const result = await login(email, password)

        if (result.error) {
            setError(result.error)
            setLoading(false)
        } else {
            navigate('/')
        }
    }

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault()
        setResetMessage('')
        setResetError('')
        setResetLoading(true)

        const result = await api.resetPassword(resetEmail)

        if (result.error) {
            setResetError(result.error)
        } else {
            setResetMessage(result.message || 'Password reset link sent to your email')
            setTimeout(() => {
                setShowResetPassword(false)
                setResetEmail('')
                setResetMessage('')
                setResetError('')
            }, 3000)
        }
        setResetLoading(false)
    }

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
            padding: '1rem'
        }}>
            <div style={{
                width: '100%',
                maxWidth: '400px',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                padding: '2.5rem',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)'
            }}>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <div style={{ 
                        width: 64, 
                        height: 64, 
                        margin: '0 auto 1rem',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)'
                    }}>
                        <img src="/logo.png" alt="RailSahayak" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                    <h1 style={{
                        fontSize: '1.75rem',
                        fontWeight: 700,
                        color: '#fff',
                        marginBottom: '0.5rem'
                    }}>
                        RailSahayak
                    </h1>
                    <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                        Contractor Evaluation System
                    </p>
                </div>

                {/* Biometric Login Button (if available and enabled) */}
                {biometric.isAvailable && biometric.isEnabled && (
                    <>
                        <button
                            onClick={handleBiometricLogin}
                            disabled={biometricLoading}
                            style={{
                                width: '100%',
                                padding: '1rem',
                                borderRadius: '12px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #44c9ab 0%, #36a98f 100%)',
                                color: '#fff',
                                fontSize: '1rem',
                                fontWeight: 600,
                                cursor: biometricLoading ? 'not-allowed' : 'pointer',
                                opacity: biometricLoading ? 0.7 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.75rem',
                                marginBottom: '1.5rem',
                                boxShadow: '0 4px 20px rgba(68, 201, 171, 0.3)'
                            }}
                        >
                            <span style={{ fontSize: '1.5rem' }}>
                                {biometric.biometryType?.includes('Face') ? '👤' : '👆'}
                            </span>
                            {biometricLoading 
                                ? 'Authenticating...' 
                                : `Sign in with ${biometric.biometryType || 'Biometrics'}`
                            }
                        </button>

                        {/* Divider */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '1.5rem',
                            gap: '1rem'
                        }}>
                            <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.15)' }} />
                            <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem' }}>
                                or use password
                            </span>
                            <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.15)' }} />
                        </div>
                    </>
                )}

                {/* Error Message */}
                {error && (
                    <div data-testid="login-error" style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '10px',
                        padding: '0.75rem 1rem',
                        marginBottom: '1.5rem',
                        color: '#ef4444',
                        fontSize: '0.9rem'
                    }}>
                        {error}
                    </div>
                )}

                {/* Login Form */}
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '0.9rem',
                            fontWeight: 500
                        }}>
                            Email
                        </label>
                        <input
                            data-testid="login-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            required
                            style={{
                                width: '100%',
                                padding: '0.875rem 1rem',
                                borderRadius: '10px',
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                background: 'rgba(255, 255, 255, 0.05)',
                                color: '#fff',
                                fontSize: '1rem',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <label style={{
                            display: 'block',
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '0.9rem',
                            fontWeight: 500
                        }}>
                            Password
                        </label>
                            <button
                                type="button"
                                data-testid="forgot-password"
                                onClick={() => {
                                    setShowResetPassword(true)
                                    setResetEmail(email)
                                    setResetError('')
                                    setResetMessage('')
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#3b82f6',
                                    fontSize: '0.85rem',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    padding: 0
                                }}
                            >
                                Forgot password?
                            </button>
                        </div>
                        <input
                            data-testid="login-password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                            style={{
                                width: '100%',
                                padding: '0.875rem 1rem',
                                borderRadius: '10px',
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                background: 'rgba(255, 255, 255, 0.05)',
                                color: '#fff',
                                fontSize: '1rem',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%',
                            padding: '0.875rem',
                            borderRadius: '10px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                            color: '#fff',
                            fontSize: '1rem',
                            fontWeight: 600,
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                            transition: 'all 0.2s'
                        }}
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>


            </div>

            {/* Reset Password Modal */}
            {showResetPassword && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '1rem'
                }}>
                    <div style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '20px',
                        padding: '2rem',
                        maxWidth: '400px',
                        width: '100%',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h2 style={{ color: '#fff', margin: 0 }}>Reset Password</h2>
                            <button
                                onClick={() => {
                                    setShowResetPassword(false)
                                    setResetEmail('')
                                    setResetMessage('')
                                    setResetError('')
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    padding: 0,
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {resetMessage && (
                            <div style={{
                                background: 'rgba(34, 197, 94, 0.1)',
                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                borderRadius: '10px',
                                padding: '0.75rem 1rem',
                                marginBottom: '1rem',
                                color: '#22c55e',
                                fontSize: '0.9rem'
                }}>
                                {resetMessage}
                            </div>
                        )}

                        {resetError && (
                            <div data-testid="reset-error" style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                borderRadius: '10px',
                                padding: '0.75rem 1rem',
                                marginBottom: '1rem',
                                color: '#ef4444',
                                fontSize: '0.9rem'
                            }}>
                                {resetError}
                            </div>
                        )}

                        <form onSubmit={handleResetPassword}>
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    fontSize: '0.9rem',
                                    fontWeight: 500
                                }}>
                                    Email
                                </label>
                                <input
                                    data-testid="reset-email"
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.875rem 1rem',
                                        borderRadius: '10px',
                                        border: '1px solid rgba(255, 255, 255, 0.15)',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        color: '#fff',
                                        fontSize: '1rem',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowResetPassword(false)
                                        setResetEmail('')
                                        setResetMessage('')
                                        setResetError('')
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '0.875rem',
                                        borderRadius: '10px',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        background: 'transparent',
                                        color: '#fff',
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={resetLoading}
                                    style={{
                                        flex: 1,
                                        padding: '0.875rem',
                                        borderRadius: '10px',
                                        border: 'none',
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                                        color: '#fff',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        cursor: resetLoading ? 'not-allowed' : 'pointer',
                                        opacity: resetLoading ? 0.7 : 1
                                    }}
                                >
                                    {resetLoading ? 'Sending...' : 'Send Reset Link'}
                                </button>
                            </div>
                        </form>
                    </div>
            </div>
            )}
        </div>
    )
}
